import { Component } from '@theme/component';

/** @extends {Component<{ navigation: HTMLElement, scrollButton: HTMLButtonElement }>} */
class CollectionNavigationScroll extends Component {
  requiredRefs = ['navigation', 'scrollButton'];
  #controller;
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#mount();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#mount();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }

  #mount() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const navigation = this.refs.navigation;
    navigation.addEventListener('scroll', this.#update, { passive: true, signal: this.#controller.signal });
    this.#observer = new ResizeObserver(this.#update);
    this.#observer.observe(this);
    this.#observer.observe(navigation);
    for (const link of navigation.children) this.#observer.observe(link);
    this.#update();
    const active = navigation.querySelector('[aria-current="page"]');
    if (active instanceof HTMLElement && navigation.clientWidth) {
      const item = active.getBoundingClientRect();
      const viewport = navigation.getBoundingClientRect();
      if (item.left < viewport.left || item.right > viewport.right) {
        const delta = item.left < viewport.left ? item.left - viewport.left : item.right - viewport.right;
        navigation.scrollBy({ left: delta, behavior: 'instant' });
      }
    }
  }

  #update = () => {
    if (!this.isConnected || !this.clientWidth) return;
    const { navigation, scrollButton } = this.refs;
    const overflowing = navigation.scrollWidth > this.clientWidth + 2;
    scrollButton.hidden = !overflowing;
    const atEnd = Math.abs(navigation.scrollLeft) >= navigation.scrollWidth - navigation.clientWidth - 3;
    const back = overflowing && atEnd;
    const label = back ? scrollButton.dataset.previousLabel : scrollButton.dataset.nextLabel;
    if (scrollButton.dataset.back !== String(back)) scrollButton.dataset.back = String(back);
    if (label && scrollButton.getAttribute('aria-label') !== label) scrollButton.setAttribute('aria-label', label);
  };

  /** Scrolls to the hidden categories or returns to the beginning. */
  handleScroll() {
    const { navigation, scrollButton } = this.refs;
    const max = navigation.scrollWidth - navigation.clientWidth;
    const direction = getComputedStyle(navigation).direction === 'rtl' ? -1 : 1;
    const left = scrollButton.dataset.back === 'true' ? 0 : direction * max;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    navigation.scrollTo({ left, behavior });
  }
}

if (!customElements.get('collection-navigation-scroll-component')) {
  customElements.define('collection-navigation-scroll-component', CollectionNavigationScroll);
}
